import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTerminals } from "features/terminals/useTerminals";
import { useMerchantNames } from "features/merchants/useMerchantNames";
import type { TerminalRecord } from "components/terminal-row/TerminalRow";
import type { PosByTreasury } from "utils/AxiosUtil";

/**
 * Map the recent/looked-up terminal shape onto the row model TerminalRow renders — shared with
 * useTerminalLookup so a chain-found terminal renders identically to one from the recent list.
 */
export function toTerminalRecord(
  t: PosByTreasury,
  merchantsMap: Record<string, string>,
  pendingIds: ReadonlySet<string> = new Set(),
): TerminalRecord {
  return {
    serialId: t.serialId,
    serial: t.serial,
    merchantId: t.merchantId,
    merchantName: t.unassigned
      ? "—"
      : t.merchantName || merchantsMap[t.merchantId] || t.merchantId,
    merchantNamePending:
      !t.unassigned && !t.merchantName && pendingIds.has(t.merchantId),
    status: t.statusLabel,
    registeredAt: t.registeredAt,
    vendorDigit: t.vendorDigit,
    typeDigit: t.typeDigit,
  };
}

export interface UseIssuerTerminalsReturn {
  terminals: TerminalRecord[];
  loadingTerminals: boolean;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useIssuerTerminals(): UseIssuerTerminalsReturn {
  const queryClient = useQueryClient();

  const {
    data: rawTerminals,
    isLoading: terminalsLoading,
    error: terminalsError,
  } = useTerminals();

  // Only terminals the backend sent without a name need resolving, from the recent map or on-chain.
  const unnamedMerchantIds = useMemo(
    () =>
      rawTerminals
        ?.filter((t: PosByTreasury) => !t.unassigned && !t.merchantName)
        .map((t: PosByTreasury) => t.merchantId) ?? [],
    [rawTerminals],
  );
  const { namesMap, pendingIds } = useMerchantNames(unnamedMerchantIds);

  const terminals: TerminalRecord[] = useMemo(
    () =>
      rawTerminals?.map((t: PosByTreasury) =>
        toTerminalRecord(t, namesMap, pendingIds),
      ) ?? [],
    [rawTerminals, namesMap, pendingIds],
  );

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["terminals"] });
    queryClient.invalidateQueries({ queryKey: ["terminalsCount"] });
  };

  return {
    terminals,
    loadingTerminals: terminalsLoading,
    isLoading: terminalsLoading,
    error: terminalsError ? String(terminalsError) : null,
    refresh,
  };
}
